import { useEffect, useMemo, useState } from "react";
import { Nav } from "../components/Nav";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Plus, Trash2, Pencil, ArrowLeft, ShieldCheck, Search, Download, Layers } from "lucide-react";
import { toast } from "sonner";

const LEVEL_LABEL = {
    system: "System",
    socialpulse: "SocialPulse",
    partner: "Partner",
};

const LEVEL_TONE = {
    system: "bg-[hsl(0,72%,50%)] text-white",
    socialpulse: "bg-[hsl(217,91%,45%)] text-white",
    partner: "bg-[hsl(100,76%,42%)] text-white",
};

export default function AdminReserved() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [summary, setSummary] = useState({});
    const [total, setTotal] = useState(0);
    const [tenants, setTenants] = useState([]);
    const [levelFilter, setLevelFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(null);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkText, setBulkText] = useState("");
    const [bulkLevel, setBulkLevel] = useState("socialpulse");
    const [bulkCategory, setBulkCategory] = useState("");
    const [bulkTenantId, setBulkTenantId] = useState("");
    const [busy, setBusy] = useState(false);

    // form state
    const [fSlug, setFSlug] = useState("");
    const [fLevel, setFLevel] = useState("system");
    const [fCategory, setFCategory] = useState("");
    const [fNote, setFNote] = useState("");
    const [fTenantId, setFTenantId] = useState("");

    useEffect(() => {
        if (!loading && (!user || user.role !== "admin")) navigate("/dashboard");
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const params = {};
            if (levelFilter !== "all") params.level = levelFilter;
            if (query.trim()) params.q = query.trim();
            const [{ data }, { data: tData }] = await Promise.all([
                api.get("/admin/reserved-slugs", { params }),
                api.get("/admin/tenants"),
            ]);
            setItems(data.items || []);
            setSummary(data.summary || {});
            setTotal(data.total || 0);
            setTenants(tData.items || []);
        } catch (e) {
            toast.error("Failed to load reserved slugs");
        }
    };

    useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user, levelFilter]);

    const grouped = useMemo(() => {
        const g = {};
        for (const it of items) {
            const key = `${it.level}::${it.category || "Uncategorized"}`;
            (g[key] ||= []).push(it);
        }
        return g;
    }, [items]);

    const openCreate = () => {
        setFSlug(""); setFLevel("system"); setFCategory(""); setFNote(""); setFTenantId("");
        setCreating(true);
    };
    const openEdit = (it) => {
        setEditing(it);
        setFSlug(it.slug); setFLevel(it.level); setFCategory(it.category || ""); setFNote(it.note || "");
        setFTenantId(it.assigned_tenant_id || "");
    };

    const submitCreate = async () => {
        setBusy(true);
        try {
            await api.post("/admin/reserved-slugs", {
                slug: fSlug.trim(),
                level: fLevel,
                category: fCategory.trim() || undefined,
                note: fNote.trim() || undefined,
                assigned_tenant_id: fLevel === "partner" && fTenantId ? fTenantId : undefined,
            });
            toast.success("Reserved slug added");
            setCreating(false);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to add");
        } finally { setBusy(false); }
    };

    const submitEdit = async () => {
        if (!editing) return;
        setBusy(true);
        try {
            await api.patch(`/admin/reserved-slugs/${encodeURIComponent(editing.slug)}`, {
                level: fLevel,
                category: fCategory.trim() || undefined,
                note: fNote.trim() || undefined,
                assigned_tenant_id: fLevel === "partner" ? (fTenantId || "") : "",
            });
            toast.success("Reserved slug updated");
            setEditing(null);
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to update");
        } finally { setBusy(false); }
    };

    const submitBulk = async () => {
        const slugs = bulkText.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);
        if (!slugs.length) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/reserved-slugs/bulk", {
                slugs,
                level: bulkLevel,
                category: bulkCategory.trim() || undefined,
                assigned_tenant_id: bulkLevel === "partner" && bulkTenantId ? bulkTenantId : undefined,
            });
            toast.success(`Added ${data.counts.added}, skipped ${data.counts.skipped}, invalid ${data.counts.invalid}`);
            setBulkOpen(false);
            setBulkText("");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to bulk add");
        } finally { setBusy(false); }
    };

    const exportCsv = async () => {
        try {
            const res = await api.get("/admin/reserved-slugs/export.csv", { responseType: "blob" });
            const blob = new Blob([res.data], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `splink-reserved-slugs-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("CSV downloaded");
        } catch (e) {
            toast.error("Export failed");
        }
    };

    const del = async (slug) => {
        if (!window.confirm(`Remove reservation for "${slug}"? Users will be able to claim this alias.`)) return;
        try {
            await api.delete(`/admin/reserved-slugs/${encodeURIComponent(slug)}`);
            toast.success("Reservation removed");
            load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Failed to delete");
        }
    };

    const onSearch = (e) => {
        e.preventDefault();
        load();
    };

    if (loading || !user || user.role !== "admin") {
        return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-7xl px-6 py-10">
                <Button asChild variant="ghost" size="sm" className="mb-6" data-testid="back-to-admin">
                    <Link to="/admin"><ArrowLeft className="mr-2 h-4 w-4" /> Back to admin</Link>
                </Button>

                <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin console</div>
                        <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter" data-testid="reserved-heading">
                            Reserved slugs
                        </h1>
                        <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                            Protected names that cannot be claimed as short-link aliases.
                            System-level names are blocked for everyone; SocialPulse and Partner
                            names are blocked for regular users.
                        </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
                            <Download className="mr-2 h-4 w-4" /> Export CSV
                        </Button>
                        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" data-testid="bulk-reserve-btn">
                                    <Layers className="mr-2 h-4 w-4" /> Bulk reserve
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-lg">
                                <DialogHeader>
                                    <DialogTitle>Bulk reserve slugs</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label>Slugs</Label>
                                        <Textarea
                                            rows={7}
                                            value={bulkText}
                                            onChange={(e) => setBulkText(e.target.value)}
                                            placeholder={"newsite\nbrandname\nlaunch2027\n..."}
                                            className="mt-2 font-mono text-sm"
                                            data-testid="bulk-textarea"
                                        />
                                        <p className="mt-1 text-[11px] text-muted-foreground">
                                            Paste one per line (also accepts commas or spaces). Duplicates and invalid entries are skipped.
                                        </p>
                                    </div>
                                    <div>
                                        <Label>Level</Label>
                                        <Select value={bulkLevel} onValueChange={setBulkLevel}>
                                            <SelectTrigger className="mt-2" data-testid="bulk-level-select"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="system">System</SelectItem>
                                                <SelectItem value="socialpulse">SocialPulse</SelectItem>
                                                <SelectItem value="partner">Partner</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label>Category</Label>
                                        <Input value={bulkCategory} onChange={(e) => setBulkCategory(e.target.value)}
                                            placeholder="e.g. Q1 launches"
                                            className="mt-2" data-testid="bulk-category-input" />
                                    </div>
                                    {bulkLevel === "partner" && (
                                        <div>
                                            <Label>Assign to partner tenant</Label>
                                            <Select value={bulkTenantId} onValueChange={setBulkTenantId}>
                                                <SelectTrigger className="mt-2" data-testid="bulk-tenant-select"><SelectValue placeholder="Select tenant..." /></SelectTrigger>
                                                <SelectContent>
                                                    {tenants.map((t) => (
                                                        <SelectItem key={t.tenant_id} value={t.tenant_id}>{t.name} ({t.slug})</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <Button onClick={submitBulk} disabled={busy || !bulkText.trim()} className="w-full" data-testid="submit-bulk-btn">
                                        {busy ? "Reserving..." : `Reserve ${bulkText.split(/[\s,]+/).filter((s) => s.trim()).length} slugs`}
                                    </Button>
                                </div>
                            </DialogContent>
                        </Dialog>
                        <Dialog open={creating} onOpenChange={setCreating}>
                            <DialogTrigger asChild>
                                <Button onClick={openCreate} data-testid="add-reserved-btn">
                                    <Plus className="mr-2 h-4 w-4" /> Add reservation
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Add reserved slug</DialogTitle>
                                </DialogHeader>
                                <ReservedForm
                                    slug={fSlug} setSlug={setFSlug}
                                    level={fLevel} setLevel={setFLevel}
                                    category={fCategory} setCategory={setFCategory}
                                    note={fNote} setNote={setFNote}
                                    tenantId={fTenantId} setTenantId={setFTenantId}
                                    tenants={tenants}
                                />
                                <Button onClick={submitCreate} disabled={busy || !fSlug.trim()} data-testid="submit-add-reserved">
                                    {busy ? "Adding..." : "Add reservation"}
                                </Button>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Summary */}
                <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <Metric label="Total" value={total} testId="metric-total" />
                    <Metric label="System" value={sum(summary.system)} testId="metric-system" />
                    <Metric label="SocialPulse" value={sum(summary.socialpulse)} testId="metric-socialpulse" />
                    <Metric label="Partner" value={sum(summary.partner)} testId="metric-partner" />
                </div>

                {/* Filter tabs + search */}
                <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <Tabs value={levelFilter} onValueChange={setLevelFilter} className="w-fit">
                        <TabsList data-testid="level-tabs">
                            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
                            <TabsTrigger value="system" data-testid="tab-system">System</TabsTrigger>
                            <TabsTrigger value="socialpulse" data-testid="tab-socialpulse">SocialPulse</TabsTrigger>
                            <TabsTrigger value="partner" data-testid="tab-partner">Partner</TabsTrigger>
                        </TabsList>
                    </Tabs>
                    <form onSubmit={onSearch} className="flex gap-2">
                        <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
                            <Input
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search slugs..."
                                className="h-10 w-64 pl-9 font-mono"
                                data-testid="search-input"
                            />
                        </div>
                        <Button type="submit" variant="outline" data-testid="search-submit-btn">Search</Button>
                    </form>
                </div>

                {/* Grouped table */}
                <div className="mt-6 space-y-6">
                    {Object.keys(grouped).length === 0 ? (
                        <div className="border border-border bg-card p-8 text-center text-sm text-muted-foreground">
                            No reservations match your filters.
                        </div>
                    ) : (
                        Object.entries(grouped)
                            .sort(([a], [b]) => a.localeCompare(b))
                            .map(([key, list]) => {
                                const [level, category] = key.split("::");
                                return (
                                    <div key={key} className="border border-border bg-card" data-testid={`group-${level}-${category}`}>
                                        <div className="flex items-center justify-between border-b border-border px-5 py-3">
                                            <div className="flex items-center gap-3">
                                                <span className={`inline-flex items-center px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest ${LEVEL_TONE[level] || "bg-muted"}`}>
                                                    <ShieldCheck className="mr-1 h-3 w-3" />
                                                    {LEVEL_LABEL[level] || level}
                                                </span>
                                                <div className="font-display text-lg font-semibold">{category}</div>
                                            </div>
                                            <div className="font-mono text-xs text-muted-foreground">{list.length} slugs</div>
                                        </div>
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>Slug</TableHead>
                                                    <TableHead>Note</TableHead>
                                                    <TableHead>Source</TableHead>
                                                    <TableHead>Updated</TableHead>
                                                    <TableHead className="text-right">Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {list.map((it) => (
                                                    <TableRow key={it.slug} data-testid={`reserved-row-${it.slug}`}>
                                                        <TableCell className="font-mono font-medium">{it.slug}</TableCell>
                                                        <TableCell className="max-w-[240px] truncate text-sm text-muted-foreground">
                                                            {it.note || "—"}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant="outline" className="font-mono text-[10px] uppercase">
                                                                {it.source || "seed"}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell className="font-mono text-xs text-muted-foreground">
                                                            {formatDate(it.updated_at)}
                                                            {it.updated_by && <div className="text-[10px]">by {it.updated_by}</div>}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <Button size="icon" variant="ghost" onClick={() => openEdit(it)} aria-label="Edit" data-testid={`edit-${it.slug}`}>
                                                                    <Pencil className="h-4 w-4" />
                                                                </Button>
                                                                <Button size="icon" variant="ghost" onClick={() => del(it.slug)} aria-label="Delete" data-testid={`del-${it.slug}`}>
                                                                    <Trash2 className="h-4 w-4" />
                                                                </Button>
                                                            </div>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                );
                            })
                    )}
                </div>
            </div>

            {/* Edit dialog */}
            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit reservation: <span className="font-mono">{editing?.slug}</span></DialogTitle>
                    </DialogHeader>
                    <ReservedForm
                        slug={fSlug} setSlug={setFSlug} slugDisabled
                        level={fLevel} setLevel={setFLevel}
                        category={fCategory} setCategory={setFCategory}
                        note={fNote} setNote={setFNote}
                    />
                    <Button onClick={submitEdit} disabled={busy} data-testid="submit-edit-reserved">
                        {busy ? "Saving..." : "Save changes"}
                    </Button>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function ReservedForm({ slug, setSlug, slugDisabled, level, setLevel, category, setCategory, note, setNote, tenantId, setTenantId, tenants }) {
    return (
        <div className="space-y-3">
            <div>
                <Label htmlFor="rs-slug">Slug</Label>
                <Input
                    id="rs-slug"
                    value={slug}
                    onChange={(e) => setSlug(e.target.value)}
                    disabled={slugDisabled}
                    placeholder="e.g. mycompany"
                    className="mt-2 font-mono"
                    data-testid="reserved-slug-input"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">Alphanumeric, dashes and underscores. Stored lowercase.</p>
            </div>
            <div>
                <Label htmlFor="rs-level">Level</Label>
                <Select value={level} onValueChange={setLevel}>
                    <SelectTrigger id="rs-level" className="mt-2" data-testid="reserved-level-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="system">System — blocks everyone</SelectItem>
                        <SelectItem value="socialpulse">SocialPulse — blocks users (admin can override)</SelectItem>
                        <SelectItem value="partner">Partner — reserved for verified partners</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            {level === "partner" && setTenantId && (
                <div>
                    <Label htmlFor="rs-tenant">Assign to partner tenant</Label>
                    <Select value={tenantId || ""} onValueChange={setTenantId}>
                        <SelectTrigger id="rs-tenant" className="mt-2" data-testid="reserved-tenant-select">
                            <SelectValue placeholder="Select tenant..." />
                        </SelectTrigger>
                        <SelectContent>
                            {(tenants || []).map((t) => (
                                <SelectItem key={t.tenant_id} value={t.tenant_id}>
                                    {t.name} ({t.slug})
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <p className="mt-1 text-[11px] text-muted-foreground">Track which partner this slug belongs to.</p>
                </div>
            )}
            <div>
                <Label htmlFor="rs-cat">Category</Label>
                <Input id="rs-cat" value={category} onChange={(e) => setCategory(e.target.value)}
                    placeholder="e.g. Marketing, Kerala, Core"
                    className="mt-2" data-testid="reserved-category-input" />
            </div>
            <div>
                <Label htmlFor="rs-note">Note</Label>
                <Textarea id="rs-note" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Reason for reservation, partner assignment, etc."
                    data-testid="reserved-note-input" />
            </div>
        </div>
    );
}

function Metric({ label, value, testId }) {
    return (
        <div className="border border-border bg-card p-5" data-testid={testId}>
            <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
            <div className="mt-3 font-display text-3xl font-bold">{value}</div>
        </div>
    );
}

function sum(obj) {
    if (!obj) return 0;
    return Object.values(obj).reduce((a, b) => a + b, 0);
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
    } catch { return iso; }
}
